/**
 * Импорт данных в блок-таблицу (fkTable): чистая логика без React и DOM.
 *
 *   • разбор источников → матрица строк (`Matrix`):
 *       parseDelimited  — TSV (копия из Excel/Google Таблиц) и CSV (`,` / `;` / таб);
 *       parseHtmlTable  — первая <table> из буфера обмена (text/html);
 *       parseXlsx       — файл .xlsx (exceljs подгружается лениво);
 *   • inferColumnType / inferColumn — тип колонки по значениям;
 *   • buildTableFromMatrix — матрица → {columns, rows} в формате FkTableData;
 *   • planMerge / mergeIntoTable — «заменить» или «добавить строки»;
 *   • applyPasteMatrix — вставка нескольких ячеек из буфера в сетку «как в Google Таблицах».
 *
 * Формат данных тот же, что у блока (ячейки — строки в `row.cells[colId]`, дата —
 * `YYYY-MM-DD`, число — строкой с точкой), поэтому сериализация в markdown и
 * бэкенд-читалка не меняются.
 */
import type { FkColumn, FkFieldType, FkRow, FkTableData, FkView } from './fk-table';
import { uid as defaultUid } from './fk-uid';

// ─────────────────────────── Лимиты ───────────────────────────

/** Не больше строк данных за один импорт (и в таблице после «Добавить»). */
export const IMPORT_MAX_ROWS = 2000;
/** Не больше столбцов. */
export const IMPORT_MAX_COLS = 50;
/** Не больше символов в ячейке. */
export const IMPORT_MAX_CELL = 5000;

/** Select выводим, только если различных значений не больше этого. */
const SELECT_MAX_OPTIONS = 8;
/** …и непустых значений не меньше этого (иначе «повторов» не видно). */
const SELECT_MIN_FILLED = 4;
/** Длиннее — это уже не вариант выбора, а текст. */
const SELECT_MAX_LEN = 40;
/** Из .xlsx читаем не больше (страховка памяти); сверх — отрезаем и предупреждаем. */
const XLSX_READ_MAX_ROWS = 20000;
const XLSX_READ_MAX_COLS = 200;

/** Матрица значений: строки × столбцы, все ячейки — строки. */
export type Matrix = string[][];

/** Типы, которые умеем выводить сами (остальные пользователь выбирает вручную). */
export type ImportFieldType = 'text' | 'number' | 'date' | 'select';

/** Сколько отрезано по лимитам. */
export interface ImportCut {
  rows: number;
  cols: number;
  /** Сколько ячеек было длиннее IMPORT_MAX_CELL и обрезано. */
  cells: number;
}

export interface ImportedTable {
  columns: FkColumn[];
  rows: FkRow[];
  cut: ImportCut;
}

export interface ImportBuildOptions {
  /** Первая строка — заголовки. */
  headerRow: boolean;
  /** Генератор id (по умолчанию общий с блоком — fk-uid). */
  uid?: (prefix: string) => string;
  /** Ручной выбор типа по индексу столбца (undefined — вывести самим). */
  types?: (FkFieldType | undefined)[];
}

// ─────────────────────────── Очистка значений ───────────────────────────

/** Обрезка по лимиту без разрыва суррогатной пары. */
function cutCell(s: string): string {
  let out = s.slice(0, IMPORT_MAX_CELL);
  const last = out.charCodeAt(out.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) out = out.slice(0, -1);
  return out;
}

/**
 * Приводит значение ячейки к «человеческому» виду: единые переводы строк,
 * неразрывные пробелы → обычные, невидимые символы убраны, края обрезаны.
 */
export function cleanCell(v: string): string {
  return String(v ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/[   ]/g, ' ')
    .replace(/[​-‍﻿]/g, '')
    .trim();
}

const isBlank = (v: string | undefined): boolean => !v || v.trim() === '';

/**
 * Отрезает хвостовые пустые строки и пустые столбцы, выравнивает строки до
 * одной ширины. Пустая матрица → [].
 */
export function trimMatrix(m: Matrix): Matrix {
  let rows = m.length;
  while (rows > 0 && m[rows - 1].every(isBlank)) rows--;
  if (rows === 0) return [];
  let width = 0;
  for (let r = 0; r < rows; r++) {
    const row = m[r];
    for (let c = row.length - 1; c >= width; c--) {
      if (!isBlank(row[c])) { width = c + 1; break; }
    }
  }
  if (width === 0) return [];
  const out: Matrix = [];
  for (let r = 0; r < rows; r++) {
    const row = m[r];
    const cells = new Array<string>(width);
    for (let c = 0; c < width; c++) cells[c] = row[c] ?? '';
    out.push(cells);
  }
  return out;
}

// ─────────────────────────── TSV / CSV ───────────────────────────

/**
 * Режет текст на записи по разделителю `d` с учётом кавычек: `"a ""b"" c"`,
 * переводы строк внутри кавычек остаются в значении. Разделитель записей —
 * \n, \r\n или \r. Незакрытая кавычка — берём остаток текста.
 */
function splitRecords(s: string, d: string, maxRecords = Infinity): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let atStart = true; // в начале поля — только здесь кавычка открывает «quoted»
  let i = 0;
  const n = s.length;
  while (i < n) {
    const ch = s[i];
    if (atStart && ch === '"') {
      i++;
      let val = '';
      while (i < n) {
        const c = s[i];
        if (c === '"') {
          if (s[i + 1] === '"') { val += '"'; i += 2; continue; }
          i++;
          break;
        }
        val += c;
        i++;
      }
      field = val;
      atStart = false;
      // После закрывающей кавычки до разделителя — дописываем как есть (мягкий разбор).
      while (i < n && s[i] !== d && s[i] !== '\n' && s[i] !== '\r') { field += s[i]; i++; }
      continue;
    }
    if (ch === d) { row.push(field); field = ''; atStart = true; i++; continue; }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      atStart = true;
      if (rows.length >= maxRecords) return rows;
      continue;
    }
    field += ch;
    atStart = false;
    i++;
  }
  // Последняя запись: не добавляем «пустую» после завершающего перевода строки.
  if (row.length > 0 || field !== '' || !atStart) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * Автоопределение разделителя: пробуем таб, `;`, `,` на первых записях и берём
 * тот, при котором больше строк той же ширины (≥ 2 столбцов). При равенстве
 * приоритет у таба (копия из Excel), затем `;`, затем `,`.
 */
function detectDelimiter(s: string): string {
  const truncated = s.length > 65536;
  const sample = truncated ? s.slice(0, 65536) : s;
  let best = '\t';
  let bestRows = 0;
  for (const d of ['\t', ';', ',']) {
    let rows = splitRecords(sample, d, 30);
    if (truncated && rows.length > 1) rows = rows.slice(0, -1); // последняя могла оборваться
    const w = rows[0]?.length ?? 0;
    if (w < 2) continue;
    const consistent = rows.filter((r) => r.length === w).length;
    if (consistent > bestRows) {
      best = d;
      bestRows = consistent;
    }
  }
  return best;
}

/**
 * Разбор TSV/CSV в матрицу. `delimiter` не задан — определяется сам.
 * BOM убирается, хвостовые пустые строки и столбцы отрезаются.
 */
export function parseDelimited(text: string, delimiter?: string): Matrix {
  const s = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  if (!s) return [];
  const d = delimiter ?? detectDelimiter(s);
  return trimMatrix(splitRecords(s, d));
}

/**
 * Текст файла из байтов: UTF-8 (с BOM или без), UTF-16 (Excel «Юникод-текст»),
 * иначе windows-1251 — так Excel сохраняет «CSV (разделитель — точка с запятой)».
 */
export function decodeTextFile(buf: ArrayBuffer): string {
  const u = new Uint8Array(buf);
  if (u[0] === 0xff && u[1] === 0xfe) return new TextDecoder('utf-16le').decode(u.subarray(2));
  if (u[0] === 0xfe && u[1] === 0xff) return new TextDecoder('utf-16be').decode(u.subarray(2));
  if (u[0] === 0xef && u[1] === 0xbb && u[2] === 0xbf) return new TextDecoder('utf-8').decode(u.subarray(3));
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(u);
  } catch {
    return new TextDecoder('windows-1251').decode(u);
  }
}

// ─────────────────────────── HTML-таблица ───────────────────────────

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  laquo: '«', raquo: '»', mdash: '—', ndash: '–', hellip: '…', bull: '•',
  copy: '©', reg: '®', deg: '°', times: '×', euro: '€', pound: '£', yen: '¥',
  minus: '−', middot: '·', plusmn: '±', frac12: '½', thinsp: ' ',
};

function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return m;
      return String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[e.toLowerCase()] ?? m;
  });
}

/** Есть ли в html таблица (быстрая проверка до полного разбора). */
export function hasHtmlTable(html: string): boolean {
  return /<table[\s>]/i.test(html);
}

const BLOCK_TAGS = new Set(['p', 'div', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote']);

/**
 * Первая <table> из HTML (Excel/Google Таблицы кладут её в буфер как text/html)
 * → матрица. colspan/rowspan раскладываются: значение остаётся в первой ячейке,
 * остальные — пустые. <br> и блочные теги внутри ячейки → перевод строки.
 * Нет таблицы → null. Работает без DOM, поэтому доступен и в тестах, и в воркере.
 */
export function parseHtmlTable(html: string): Matrix | null {
  const src = html
    .replace(/<!--[\s\S]*?-->/g, '') // и условные комментарии Office, и <!--StartFragment-->
    .replace(/<!\[[^\]]*\]>/g, '') // <![if !vml]> / <![endif]>
    .replace(/<\?[^>]*>/g, '')
    .replace(/<(style|script|head|template|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '');

  const TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9:-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>|([^<]+)|</g;

  const grid: string[][] = [];
  const occupied: boolean[][] = [];
  let depth = 0;
  let started = false;
  let r = -1;
  let c = 0;
  let cell: { text: string; colspan: number; rowspan: number } | null = null;

  const startRow = (): void => {
    r++;
    c = 0;
    if (!grid[r]) grid[r] = [];
    if (!occupied[r]) occupied[r] = [];
  };
  const closeCell = (): void => {
    if (!cell) return;
    if (r < 0) startRow();
    while (occupied[r][c]) c++;
    const text = cell.text
      .replace(/ /g, ' ')
      .replace(/ *\n */g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    for (let dr = 0; dr < cell.rowspan; dr++) {
      const rr = r + dr;
      if (!grid[rr]) grid[rr] = [];
      if (!occupied[rr]) occupied[rr] = [];
      for (let dc = 0; dc < cell.colspan; dc++) occupied[rr][c + dc] = true;
    }
    grid[r][c] = text;
    c += cell.colspan;
    cell = null;
  };
  const newline = (): void => {
    if (cell && cell.text !== '' && !cell.text.endsWith('\n')) cell.text += '\n';
  };

  let m: RegExpExecArray | null;
  while ((m = TOKEN.exec(src)) !== null) {
    const [, closing, tagRaw, attrs, text] = m;
    if (tagRaw === undefined) {
      // Текст (или одинокий «<»)
      if (cell) {
        const t = decodeEntities(text ?? '<').replace(/[ \t\r\n\f]+/g, ' ');
        cell.text += cell.text === '' || cell.text.endsWith('\n') ? t.replace(/^ /, '') : t;
      }
      continue;
    }
    const tag = tagRaw.toLowerCase();
    const isClose = closing === '/';
    if (tag === 'table') {
      if (!isClose) {
        depth++;
        if (depth === 1) started = true;
        else newline(); // вложенная таблица — отдельным абзацем внутри ячейки
      } else if (depth > 0) {
        depth--;
        if (depth === 0 && started) { closeCell(); break; }
        newline();
      }
      continue;
    }
    if (!started) continue;
    if (depth === 1 && (tag === 'tr' || tag === 'td' || tag === 'th')) {
      if (tag === 'tr') {
        closeCell();
        if (!isClose) startRow();
      } else if (!isClose) {
        closeCell();
        const cs = /\bcolspan\s*=\s*["']?(\d+)/i.exec(attrs)?.[1];
        const rs = /\browspan\s*=\s*["']?(\d+)/i.exec(attrs)?.[1];
        cell = {
          text: '',
          colspan: Math.min(1000, Math.max(1, cs ? parseInt(cs, 10) : 1)),
          rowspan: Math.min(10000, Math.max(1, rs ? parseInt(rs, 10) : 1)),
        };
      } else {
        closeCell();
      }
      continue;
    }
    if (tag === 'br' || tag === 'table' || tag === 'tr' || BLOCK_TAGS.has(tag)) newline();
    else if (tag === 'td' || tag === 'th') {
      // вложенная таблица: ячейки через пробел
      if (cell && cell.text !== '' && !/[\s]$/.test(cell.text)) cell.text += ' ';
    }
  }
  closeCell();

  if (!started || grid.length === 0) return null;
  let width = 0;
  for (const row of grid) if (row) width = Math.max(width, row.length);
  if (width === 0) return null;
  return Array.from({ length: grid.length }, (_, i) =>
    Array.from({ length: width }, (_, j) => grid[i]?.[j] ?? ''),
  );
}

// ─────────────────────────── Буфер обмена ───────────────────────────

/**
 * Что вставили из буфера при редактировании ячейки: html-таблица (Excel/Sheets)
 * либо текст с табами/переводами строк. Одно значение (в том числе 1×1) → null:
 * тогда работает обычная вставка в поле.
 */
export function parseClipboardMatrix(html: string, text: string): Matrix | null {
  const isMulti = (m: Matrix | null): m is Matrix => !!m && (m.length > 1 || (m[0]?.length ?? 0) > 1);
  if (html && hasHtmlTable(html)) {
    const t = parseHtmlTable(html);
    if (isMulti(t)) return t;
  }
  if (text && /[\t\r\n]/.test(text.replace(/[\r\n]+$/, ''))) {
    const t = parseDelimited(text, '\t');
    if (isMulti(t)) return t;
  }
  return null;
}

// ─────────────────────────── Числа и даты ───────────────────────────

/**
 * Число в строку без экспоненты и «хвостов» двоичной арифметики (0.1+0.2 → "0.3"):
 * 15 значащих цифр, как показывает Excel.
 */
export function formatNumberPlain(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n) && Math.abs(n) < 1e15) return String(n);
  const s = String(Number(n.toPrecision(15)));
  return /e/i.test(s) ? expandExponent(s) : s;
}

/** "1.5e-7" → "0.00000015", "1e+21" → "1000000000000000000000". */
function expandExponent(s: string): string {
  const m = /^(-?)(\d+)(?:\.(\d+))?e([+-]?\d+)$/i.exec(s);
  if (!m) return s;
  const [, sign, int, frac = '', exp] = m;
  const digits = int + frac;
  const pos = int.length + parseInt(exp, 10);
  let body: string;
  if (pos <= 0) body = '0.' + '0'.repeat(-pos) + digits;
  else if (pos >= digits.length) body = digits + '0'.repeat(pos - digits.length);
  else body = digits.slice(0, pos) + '.' + digits.slice(pos);
  return sign + body;
}

/**
 * Строка → число в виде строки с точкой, либо null, если это не число.
 * Допустимо: пробелы/неразрывные пробелы как разделители тысяч, запятая или
 * точка как десятичный разделитель, "1,234.56" и "1.234,56", минус (в том числе
 * типографский). НЕ число: "+7900…" (телефон), "007" (код с нулями), больше
 * 15 цифр (не влезет в число без потерь), "1e5", "12%", "100 ₽", "10.12.2020".
 */
export function normalizeNumber(raw: string): string | null {
  let s = raw.replace(/[−‒–]/g, '-').trim();
  if (!s) return null;
  let sign = '';
  if (s[0] === '-') { sign = '-'; s = s.slice(1); }
  if (!s) return null;

  // Пробелы допустимы только как разделитель групп по три цифры.
  if (/[\s   ]/.test(s)) {
    if (!/^\d{1,3}(?:[    ]\d{3})+(?:[.,]\d+)?$/.test(s)) return null;
    s = s.replace(/[    ]/g, '');
  }
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;

  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  let int: string;
  let frac = '';
  if (hasDot && hasComma) {
    // Десятичный разделитель — тот, что правее; другой обязан быть группами по три цифры.
    const dec = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
    const thou = dec === '.' ? ',' : '.';
    const re = new RegExp(`^(\\d{1,3}(?:\\${thou}\\d{3})+)\\${dec}(\\d+)$`);
    const mm = re.exec(s);
    if (!mm) return null;
    int = mm[1].split(thou).join('');
    frac = mm[2];
  } else if (hasDot || hasComma) {
    const sep = hasDot ? '.' : ',';
    const parts = s.split(sep);
    if (parts.length === 2) {
      if (!/^\d*$/.test(parts[0]) || !/^\d+$/.test(parts[1])) return null; // "5." не число
      int = parts[0] === '' ? '0' : parts[0];
      frac = parts[1];
    } else {
      // Несколько одинаковых разделителей — только как группы по три цифры.
      if (!new RegExp(`^\\d{1,3}(?:\\${sep}\\d{3}){2,}$`).test(s)) return null;
      int = parts.join('');
    }
  } else {
    int = s;
  }
  if (int.length > 1 && int[0] === '0') return null; // "007" — код, а не число
  if (int.length + frac.length > 15) return null;
  return sign + int + (frac ? '.' + frac : '');
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

function isRealDate(y: number, mo: number, d: number): boolean {
  if (y < 1000 || y > 9999 || mo < 1 || mo > 12 || d < 1) return false;
  const dim = new Date(Date.UTC(2000, mo, 0)).getUTCDate(); // дней в месяце (високосность — ниже)
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  return d <= (mo === 2 ? (leap ? 29 : 28) : dim);
}

/** Необязательный хвост «полночь» после даты: "00:00", "0:00:00", "T00:00:00Z". */
const MIDNIGHT = '(?:[T ]0{1,2}:00(?::00(?:\\.0+)?)?Z?)?';
const RE_DMY = new RegExp(`^(\\d{1,2})\\.(\\d{1,2})\\.(\\d{4}|\\d{2})(?:\\s*г\\.?)?(?:\\s+0{1,2}:00(?::00)?)?$`);
const RE_ISO = new RegExp(`^(\\d{4})-(\\d{1,2})-(\\d{1,2})${MIDNIGHT}$`);

/**
 * Строка → дата `YYYY-MM-DD` либо null. Форматы: ДД.ММ.ГГГГ, ГГГГ-ММ-ДД,
 * ДД.ММ.ГГ (двузначный год: 00–49 → 20xx, 50–99 → 19xx). Время допускается
 * только «полночь» (00:00): иначе информация о времени потерялась бы.
 */
export function normalizeDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  let y: number;
  let mo: number;
  let d: number;
  let m = RE_ISO.exec(s);
  if (m) {
    y = +m[1]; mo = +m[2]; d = +m[3];
  } else {
    m = RE_DMY.exec(s);
    if (!m) return null;
    d = +m[1]; mo = +m[2];
    y = +m[3];
    if (m[3].length === 2) y += y <= 49 ? 2000 : 1900;
  }
  if (!isRealDate(y, mo, d)) return null;
  return `${y}-${pad2(mo)}-${pad2(d)}`;
}

/**
 * Значение под тип колонки: число — с точкой, дата — ISO. Если не получилось
 * (значение не число / не дата) — оставляем как есть, ничего не теряем.
 */
export function convertForType(value: string, type: FkFieldType): string {
  if (!value) return value;
  if (type === 'number') return normalizeNumber(value) ?? value;
  if (type === 'date') return normalizeDate(value) ?? value;
  return value;
}

// ─────────────────────────── Вывод типа колонки ───────────────────────────

export interface InferredColumn {
  type: ImportFieldType;
  /** Для 'select' — список вариантов в порядке появления. */
  options?: string[];
}

/**
 * Тип колонки по значениям (пустые не учитываются):
 *   number — все непустые значения числа (см. normalizeNumber);
 *   date   — все непустые значения даты (см. normalizeDate);
 *   select — не больше 8 различных коротких значений, и строк заметно больше,
 *            чем вариантов (повторы есть), и это не числа/даты;
 *   text   — всё остальное.
 */
export function inferColumn(values: string[]): InferredColumn {
  const filled = values.map((v) => v.trim()).filter((v) => v !== '');
  if (filled.length === 0) return { type: 'text' };
  if (filled.every((v) => normalizeNumber(v) !== null)) return { type: 'number' };
  if (filled.every((v) => normalizeDate(v) !== null)) return { type: 'date' };
  const distinct = [...new Set(filled)];
  if (
    distinct.length <= SELECT_MAX_OPTIONS &&
    filled.length >= SELECT_MIN_FILLED &&
    filled.length >= distinct.length * 2 &&
    distinct.every((v) => v.length <= SELECT_MAX_LEN && !v.includes('\n'))
  ) {
    return { type: 'select', options: distinct };
  }
  return { type: 'text' };
}

export function inferColumnType(values: string[]): ImportFieldType {
  return inferColumn(values).type;
}

// ─────────────────────────── Матрица → таблица ───────────────────────────

/** Ключ сопоставления названий: без регистра, лишних пробелов и разницы е/ё. */
function nameKey(name: string): string {
  return name.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();
}

/** Пустые заголовки → «Поле N», повторы → «Название (2)». */
function uniqueNames(raw: string[]): string[] {
  const used = new Set<string>();
  return raw.map((n, i) => {
    const base = n.replace(/\s+/g, ' ').trim().slice(0, 200) || `Поле ${i + 1}`;
    let name = base;
    let k = 2;
    while (used.has(nameKey(name))) name = `${base} (${k++})`;
    used.add(nameKey(name));
    return name;
  });
}

/** Варианты выбора из значений колонки (select/status — целиком, multiselect — по запятой). */
function optionsFrom(values: string[], type: FkFieldType): string[] | undefined {
  if (type !== 'select' && type !== 'status' && type !== 'multiselect') return undefined;
  const set = new Set<string>();
  for (const v of values) {
    if (!v) continue;
    if (type === 'multiselect') for (const p of v.split(',')) { const t = p.trim(); if (t) set.add(t); }
    else set.add(v);
  }
  return [...set].slice(0, 200);
}

/**
 * Матрица → колонки и строки в формате FkTableData. Типы выводятся сами
 * (`opts.types[j]` переопределяет), значения нормализуются под тип. Лишнее сверх
 * лимитов (2000 строк, 50 столбцов, 5000 символов в ячейке) отрезается — сколько,
 * видно в `cut`.
 */
export function buildTableFromMatrix(matrix: Matrix, opts: ImportBuildOptions): ImportedTable {
  const gen = opts.uid ?? defaultUid;
  const cut: ImportCut = { rows: 0, cols: 0, cells: 0 };
  const m = trimMatrix(matrix);
  if (m.length === 0) return { columns: [], rows: [], cut };

  const width = m[0].length;
  const cols = Math.min(width, IMPORT_MAX_COLS);
  cut.cols = width - cols;

  const bodyAll = opts.headerRow ? m.slice(1) : m;
  const body = bodyAll.slice(0, IMPORT_MAX_ROWS);
  cut.rows = bodyAll.length - body.length;

  const names = uniqueNames(
    Array.from({ length: cols }, (_, j) => (opts.headerRow ? cleanCell(m[0][j] ?? '') : '')),
  );

  const colValues: string[][] = Array.from({ length: cols }, () => new Array<string>(body.length));
  for (let i = 0; i < body.length; i++) {
    for (let j = 0; j < cols; j++) {
      let s = cleanCell(body[i][j] ?? '');
      if (s.length > IMPORT_MAX_CELL) { s = cutCell(s); cut.cells++; }
      colValues[j][i] = s;
    }
  }

  const columns: FkColumn[] = [];
  for (let j = 0; j < cols; j++) {
    const forced = opts.types?.[j];
    let type: FkFieldType;
    let options: string[] | undefined;
    if (forced) {
      type = forced;
      options = optionsFrom(colValues[j], forced);
    } else {
      const inf = inferColumn(colValues[j]);
      type = inf.type;
      options = inf.options;
    }
    if (type === 'number' || type === 'date') {
      for (let i = 0; i < body.length; i++) colValues[j][i] = convertForType(colValues[j][i], type);
    }
    const col: FkColumn = { id: gen('c'), name: names[j], type };
    if (options) col.options = options;
    columns.push(col);
  }

  const rows: FkRow[] = body.map((_, i) => {
    const cells: Record<string, string> = {};
    for (let j = 0; j < cols; j++) {
      const v = colValues[j][i];
      if (v !== '') cells[columns[j].id] = v;
    }
    return { id: gen('r'), cells };
  });

  return { columns, rows, cut };
}

// ─────────────────────────── Слияние с существующей таблицей ───────────────────────────

export type ImportMode = 'replace' | 'append';

/** Таблица «пустая/шаблонная»: во всех ячейках пусто (или строк нет). */
export function isTableBlank(data: FkTableData): boolean {
  return data.rows.every((r) => Object.values(r.cells).every((v) => !v || String(v).trim() === ''));
}

/** Оставляет в представлении только сортировку/фильтры/группировку по существующим колонкам. */
export function sanitizeView(view: FkView | undefined, colIds: ReadonlySet<string>): FkView {
  const v = view || {};
  return {
    ...v,
    sort: v.sort && colIds.has(v.sort.col) ? v.sort : null,
    filters: (v.filters || []).filter((f) => colIds.has(f.col)),
    groupBy: v.groupBy && colIds.has(v.groupBy) ? v.groupBy : null,
  };
}

export interface MergePlan {
  data: FkTableData;
  /** Сколько строк попадёт в таблицу (replace — все новые, append — добавленные). */
  rowsAdded: number;
  /** Сколько столбцов в итоге новых (replace — все импортированные). */
  colsAdded: number;
  /** Сколько импортированных столбцов сопоставилось с существующими по названию (append). */
  colsMatched: number;
  /** Отрезано из-за суммарных лимитов таблицы (append). */
  cut: { rows: number; cols: number };
}

/**
 * Применение импорта к таблице.
 *   replace — новые колонки и строки; название/свёрнутость сохраняются, а
 *             сортировка/фильтр/группировка, ссылающиеся на удалённые колонки, сбрасываются;
 *   append  — строки идут в конец; колонки сопоставляются по названию (без регистра
 *             и лишних пробелов), несовпавшие становятся новыми колонками. Значения
 *             приводятся к типу существующей колонки, недостающие варианты select
 *             добавляются в её список. Суммарно не больше 2000 строк и 50 столбцов.
 */
export function planMerge(existing: FkTableData, imported: ImportedTable, mode: ImportMode): MergePlan {
  if (mode === 'replace') {
    const ids = new Set(imported.columns.map((c) => c.id));
    return {
      data: {
        ...existing,
        columns: imported.columns,
        rows: imported.rows,
        view: sanitizeView(existing.view, ids),
      },
      rowsAdded: imported.rows.length,
      colsAdded: imported.columns.length,
      colsMatched: 0,
      cut: { rows: 0, cols: 0 },
    };
  }

  const byKey = new Map<string, FkColumn>();
  for (const c of existing.columns) if (!byKey.has(nameKey(c.name))) byKey.set(nameKey(c.name), c);

  const target = new Map<string, string>(); // id импортированной колонки → id в таблице
  const newCols: FkColumn[] = [];
  let matched = 0;
  let colsCut = 0;
  for (const ic of imported.columns) {
    const hit = byKey.get(nameKey(ic.name));
    if (hit) {
      target.set(ic.id, hit.id);
      matched++;
    } else if (existing.columns.length + newCols.length < IMPORT_MAX_COLS) {
      newCols.push(ic);
      target.set(ic.id, ic.id);
    } else {
      colsCut++;
    }
  }

  const colById = new Map<string, FkColumn>();
  for (const c of [...existing.columns, ...newCols]) colById.set(c.id, c);

  const room = Math.max(0, IMPORT_MAX_ROWS - existing.rows.length);
  const take = imported.rows.slice(0, room);
  const optionAdds = new Map<string, Set<string>>();

  const newRows: FkRow[] = take.map((row) => {
    const cells: Record<string, string> = {};
    for (const [srcId, val] of Object.entries(row.cells)) {
      const tid = target.get(srcId);
      if (!tid) continue;
      const col = colById.get(tid)!;
      const v = convertForType(val, col.type);
      if ((col.type === 'select' || col.type === 'status') && v && !(col.options || []).includes(v)) {
        let set = optionAdds.get(tid);
        if (!set) { set = new Set(); optionAdds.set(tid, set); }
        set.add(v);
      }
      cells[tid] = v;
    }
    return { id: row.id, cells };
  });

  const columns = existing.columns.map((c) => {
    const adds = optionAdds.get(c.id);
    if (!adds) return c;
    const have = new Set(c.options || []);
    return { ...c, options: [...(c.options || []), ...[...adds].filter((o) => !have.has(o))] };
  });

  return {
    data: { ...existing, columns: [...columns, ...newCols], rows: [...existing.rows, ...newRows] },
    rowsAdded: newRows.length,
    colsAdded: newCols.length,
    colsMatched: matched,
    cut: { rows: imported.rows.length - take.length, cols: colsCut },
  };
}

export function mergeIntoTable(existing: FkTableData, imported: ImportedTable, mode: ImportMode): FkTableData {
  return planMerge(existing, imported, mode).data;
}

// ─────────────────────────── Вставка из буфера в сетку ───────────────────────────

export type PasteResult =
  | { ok: true; data: FkTableData }
  | { ok: false; reason: 'anchor' | 'limits' };

/**
 * Раскладывает матрицу по ячейкам, начиная с (anchor.rowId, anchor.colId), вправо
 * и вниз — как вставка в Google Таблицах. Недостающие строки добавляются в конец,
 * недостающие столбцы — типа «текст» («Поле N»). Значения приводятся к типу целевой
 * колонки (число с точкой, дата ISO), новые варианты select добавляются в её список.
 *
 * `visibleRowIds` / `visibleColIds` — порядок, в котором строки и поля видит
 * пользователь (с учётом сортировки/фильтра/группировки и скрытых полей);
 * по умолчанию — порядок данных. Не влезает в лимиты (2000 строк / 50 столбцов) →
 * `limits` (вызывающий может открыть диалог импорта с предупреждением).
 */
export function applyPasteMatrix(
  data: FkTableData,
  anchor: { rowId: string; colId: string },
  matrix: Matrix,
  opts: { visibleRowIds?: string[]; visibleColIds?: string[]; uid?: (prefix: string) => string } = {},
): PasteResult {
  const gen = opts.uid ?? defaultUid;
  const rowOrder = opts.visibleRowIds ?? data.rows.map((r) => r.id);
  const colOrder = opts.visibleColIds ?? data.columns.filter((c) => !c.hidden).map((c) => c.id);
  const startRow = rowOrder.indexOf(anchor.rowId);
  const startCol = colOrder.indexOf(anchor.colId);
  if (startRow < 0 || startCol < 0) return { ok: false, reason: 'anchor' };

  const height = matrix.length;
  const width = matrix.reduce((w, r) => Math.max(w, r.length), 0);
  if (height === 0 || width === 0) return { ok: false, reason: 'anchor' };

  const needRows = startRow + height;
  const newRowCount = Math.max(0, needRows - rowOrder.length);
  const needCols = startCol + width;
  const newColCount = Math.max(0, needCols - colOrder.length);
  if (
    (newRowCount > 0 && data.rows.length + newRowCount > IMPORT_MAX_ROWS) ||
    (newColCount > 0 && data.columns.length + newColCount > IMPORT_MAX_COLS)
  ) {
    return { ok: false, reason: 'limits' };
  }

  // Новые столбцы — типа «текст», с неповторяющимся названием.
  const usedNames = new Set(data.columns.map((c) => nameKey(c.name)));
  const addedCols: FkColumn[] = [];
  for (let k = 0; k < newColCount; k++) {
    let n = data.columns.length + k + 1;
    while (usedNames.has(nameKey(`Поле ${n}`))) n++;
    usedNames.add(nameKey(`Поле ${n}`));
    addedCols.push({ id: gen('c'), name: `Поле ${n}`, type: 'text' });
  }
  const addedRows: FkRow[] = Array.from({ length: newRowCount }, () => ({ id: gen('r'), cells: {} }));

  let columns: FkColumn[] = [...data.columns, ...addedCols];
  const rows: FkRow[] = [...data.rows, ...addedRows];
  const rowIds = [...rowOrder, ...addedRows.map((r) => r.id)];
  const colIds = [...colOrder, ...addedCols.map((c) => c.id)];

  const colById = new Map(columns.map((c) => [c.id, c]));
  const rowIdx = new Map(rows.map((r, i) => [r.id, i]));
  const optionAdds = new Map<string, Set<string>>();

  for (let i = 0; i < height; i++) {
    const rowId = rowIds[startRow + i];
    const ri = rowIdx.get(rowId);
    if (ri === undefined) continue;
    const cells = { ...rows[ri].cells };
    for (let j = 0; j < matrix[i].length; j++) {
      const colId = colIds[startCol + j];
      const col = colById.get(colId);
      if (!col) continue;
      let v = cleanCell(matrix[i][j] ?? '');
      if (v.length > IMPORT_MAX_CELL) v = cutCell(v);
      v = convertForType(v, col.type);
      if ((col.type === 'select' || col.type === 'status') && v && !(col.options || []).includes(v)) {
        let set = optionAdds.get(colId);
        if (!set) { set = new Set(); optionAdds.set(colId, set); }
        set.add(v);
      }
      cells[colId] = v;
    }
    rows[ri] = { ...rows[ri], cells };
  }

  if (optionAdds.size > 0) {
    columns = columns.map((c) => {
      const adds = optionAdds.get(c.id);
      if (!adds) return c;
      const have = new Set(c.options || []);
      return { ...c, options: [...(c.options || []), ...[...adds].filter((o) => !have.has(o))] };
    });
  }

  return { ok: true, data: { ...data, columns, rows } };
}

// ─────────────────────────── .xlsx (exceljs, лениво) ───────────────────────────

/** Данные одного листа. */
export interface XlsxSheetData {
  matrix: Matrix;
  /** Лист больше, чем мы читаем (20 000 строк / 200 столбцов) — прочитано не всё. */
  truncated: boolean;
}

export interface XlsxBook {
  /** Имена (видимых) листов. */
  sheets: string[];
  /** Прочитать лист по индексу в `sheets`. */
  read: (index: number) => XlsxSheetData;
}

export interface XlsxResult extends XlsxSheetData {
  sheets: string[];
  sheetIndex: number;
}

/** Сообщение пользователю про старый формат Excel. */
export const XLS_LEGACY_MESSAGE = 'Старый формат .xls не поддерживается. Сохраните файл как .xlsx или .csv и загрузите снова.';

/** Файл — старый бинарный Excel (OLE2: D0 CF 11 E0)? */
export function isLegacyXls(buf: ArrayBuffer): boolean {
  const u = new Uint8Array(buf, 0, Math.min(4, buf.byteLength));
  return u.length === 4 && u[0] === 0xd0 && u[1] === 0xcf && u[2] === 0x11 && u[3] === 0xe0;
}

/** Серийная дата Excel (ExcelJS отдаёт Date в UTC) → текст: дата, время или дата+время. */
function dateToText(d: Date): string {
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const h = d.getUTCHours();
  const mi = d.getUTCMinutes();
  const s = d.getUTCSeconds();
  const time = `${pad2(h)}:${pad2(mi)}${s ? ':' + pad2(s) : ''}`;
  if (y <= 1899) return time; // Excel хранит «чистое время» как долю суток от 1899-12-30
  const ymd = `${y}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return h || mi || s ? `${ymd} ${time}` : ymd;
}

/** Число из ячейки: проценты — как видны ("25%"), остальное — без мусорных хвостов. */
function numberToText(n: number, numFmt: string | undefined): string {
  if (numFmt && /%/.test(numFmt.replace(/"[^"]*"/g, ''))) {
    const dec = /\.(0+)/.exec(numFmt)?.[1].length ?? 0;
    return (n * 100).toFixed(dec) + '%';
  }
  return formatNumberPlain(n);
}

/** Значение ячейки exceljs → строка «как видно». */
function xlsxValueToText(v: unknown, numFmt: string | undefined): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return numberToText(v, numFmt);
  if (typeof v === 'boolean') return v ? 'Да' : 'Нет';
  if (v instanceof Date) return dateToText(v);
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (Array.isArray(o.richText)) return (o.richText as { text?: string }[]).map((t) => t.text ?? '').join('');
    if ('formula' in o || 'sharedFormula' in o) return xlsxValueToText(o.result, numFmt);
    if ('hyperlink' in o) return xlsxValueToText(o.text ?? o.hyperlink, numFmt);
    if ('error' in o) return String(o.error ?? '');
    if ('text' in o) return xlsxValueToText(o.text, numFmt);
  }
  return String(v);
}

/** Тип ячейки «хвост объединения» в exceljs (ValueType.Merge). */
const XLSX_MERGE_TYPE = 1;

/**
 * Открывает книгу .xlsx. exceljs (≈ 900 КБ) подгружается ЛЕНИВО — только здесь,
 * чтобы не раздувать основной бандл вики.
 */
export async function openXlsx(buf: ArrayBuffer): Promise<XlsxBook> {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  let list = wb.worksheets.filter((ws) => ws.state === 'visible' || ws.state === undefined);
  if (list.length === 0) list = wb.worksheets;
  return {
    sheets: list.map((ws) => ws.name),
    read(index: number): XlsxSheetData {
      const ws = list[index];
      if (!ws) return { matrix: [], truncated: false };
      const rowCount = Math.min(ws.rowCount, XLSX_READ_MAX_ROWS);
      const colCount = Math.min(ws.columnCount, XLSX_READ_MAX_COLS);
      const matrix: Matrix = [];
      for (let r = 1; r <= rowCount; r++) {
        const row = ws.getRow(r);
        const cells = new Array<string>(colCount);
        for (let c = 1; c <= colCount; c++) {
          const cell = row.getCell(c);
          // Объединённые ячейки: значение только в первой, остальные пустые.
          cells[c - 1] = cell.type === XLSX_MERGE_TYPE ? '' : xlsxValueToText(cell.value, cell.numFmt);
        }
        matrix.push(cells);
      }
      return {
        matrix,
        truncated: ws.rowCount > XLSX_READ_MAX_ROWS || ws.columnCount > XLSX_READ_MAX_COLS,
      };
    },
  };
}

/**
 * Первый лист (или выбранный по индексу/имени) .xlsx → матрица. Листы книги
 * возвращаются в `sheets`, чтобы показать выбор.
 */
export async function parseXlsx(buf: ArrayBuffer, sheet?: number | string): Promise<XlsxResult> {
  const book = await openXlsx(buf);
  let idx = 0;
  if (typeof sheet === 'number') idx = Math.min(Math.max(0, sheet), Math.max(0, book.sheets.length - 1));
  else if (typeof sheet === 'string') idx = Math.max(0, book.sheets.indexOf(sheet));
  const { matrix, truncated } = book.read(idx);
  return { sheets: book.sheets, sheetIndex: idx, matrix: trimMatrix(matrix), truncated };
}
